// 생성 파일 — 손으로 고치지 않는다. 원본 design-system/(FutureGen C-Planet), 생성 scripts/build-design-system.mjs(계획 문서 5.7)
import React from 'react';

const __FGCP = {};

// ── components/AlertRow/AlertRow.jsx
;(() => {
const SEV = {
  crit: 'var(--crit)', warn: 'var(--warn)', info: 'var(--info)', resolved: 'var(--ink-3)',
};

function AlertRow({
  severity = 'info', title, meta = [], onDismiss,
  divider = false, style, ...rest
}) {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', gap: 'var(--s-3)', padding: 'var(--s-3)',
        borderRadius: 'var(--r-sm)',
        borderTop: divider ? '1px solid var(--line)' : 0,
        background: hover ? 'var(--hover)' : 'transparent',
        transition: 'background var(--dur-fast) var(--ease)',
        ...style,
      }}
      {...rest}
    >
      <span style={{
        width: 7, height: 7, marginTop: 5, flex: 'none',
        background: SEV[severity] || SEV.info,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)',
          letterSpacing: '-.01em', color: 'var(--ink)',
        }}>{title}</div>
        {meta.length > 0 && (
          <div style={{
            display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 3,
            fontFamily: 'var(--font-num)', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)',
          }}>
            {meta.map((m, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span aria-hidden="true">·</span>}
                <span>{m}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss alert"
          style={{
            border: 0, background: 'none', cursor: 'pointer',
            fontSize: 15, lineHeight: 1, padding: '0 3px',
            color: 'var(--ink-3)', opacity: hover ? 1 : 0,
            transition: 'opacity var(--dur-fast) var(--ease)',
          }}
        >×</button>
      )}
    </div>
  );
}
__FGCP.AlertRow = AlertRow;
})();

// ── components/Button/Button.jsx
;(() => {
const VARIANTS = {
  primary:   { bg: 'var(--acc-graph)', fg: 'var(--acc-ink)', border: 'transparent' },
  secondary: { bg: 'transparent', fg: 'var(--ink)', border: 'var(--line-strong)' },
  ghost:     { bg: 'transparent', fg: 'var(--acc)', border: 'transparent' },
  danger:    { bg: 'var(--crit-tint)', fg: 'var(--crit)', border: 'transparent' },
};
const SIZES = { sm: { padding: '6px 13px', fontSize: 'var(--fs-sm)' }, md: { padding: '9px 18px', fontSize: 'var(--fs-body)' }, lg: { padding: '13px 26px', fontSize: 'var(--fs-lede)' } };

function Button({ variant = 'primary', size = 'md', disabled = false, children, style, ...rest }) {
  const v = VARIANTS[variant] || VARIANTS.primary;
  const s = SIZES[size] || SIZES.md;
  return (
    <button
      disabled={disabled}
      style={{
        background: disabled ? 'var(--hover)' : v.bg,
        color: disabled ? 'var(--ink-3)' : v.fg,
        border: '1px solid ' + (disabled ? 'var(--line)' : v.border),
        font: 'inherit', fontFamily: 'var(--font-ui)',
        fontWeight: 'var(--fw-semibold)', lineHeight: 1.2,
        padding: s.padding, fontSize: s.fontSize,
        borderRadius: 'var(--r-ctl)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background var(--dur-base) var(--ease), border-color var(--dur-base) var(--ease)',
        ...style,
      }}
      {...rest}
    >{children}</button>
  );
}
__FGCP.Button = Button;
})();

// ── components/Callout/Callout.jsx
;(() => {
const TONES = { accent: 'var(--acc-graph)', ok: 'var(--ok)', warn: 'var(--warn)', crit: 'var(--crit)' };

/** Aside. No background — a hairline above and below, and the mark's
 *  square as the only colour. */
function Callout({ label, tone = 'accent', children, style, ...rest }) {
  return (
    <div style={{
      padding: 'var(--s-4) 0',
      borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)',
      breakInside: 'avoid', ...style,
    }} {...rest}>
      {label && <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)',
        color: 'var(--ink)', marginBottom: 'var(--s-2)',
      }}>
        <span aria-hidden="true" style={{ width: 8, height: 8, background: TONES[tone] || TONES.accent, flex: 'none' }} />
        {label}
      </div>}
      <div style={{ fontSize: 'var(--fs-body)', lineHeight: 'var(--lh-body)', color: 'var(--ink-2)', maxWidth: '44em' }}>{children}</div>
    </div>
  );
}
__FGCP.Callout = Callout;
})();

// ── components/Card/Card.jsx
;(() => {
function Card({ accent = false, interactive = false, children, style, ...rest }) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid ' + (accent ? 'var(--acc-graph)' : 'var(--line)'),
        borderRadius: 'var(--r-panel)',
        padding: 'var(--s-5)',
        boxShadow: 'var(--elev)',
        cursor: interactive ? 'pointer' : undefined,
        transition: 'border-color var(--dur-base) var(--ease)',
        ...style,
      }}
      {...rest}
    >{children}</div>
  );
}
__FGCP.Card = Card;
})();

// ── components/Checkbox/Checkbox.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}

function Checkbox({
  checked, defaultChecked = false, indeterminate = false, onChange,
  disabled = false, size = 'md', label, description, id, style, ...rest
}) {
  injectOnce('fgcp-focus', FOCUS_CSS);
  const [on, set] = useControl(checked, defaultChecked, onChange);
  const [hover, setHover] = React.useState(false);
  const auto = React.useId();
  const cid = id || auto;
  const px = size === 'sm' ? 14 : 16;
  const filled = on || indeterminate;
  const hoverProps = { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) };

  const box = (
    <button
      id={cid} type="button" role="checkbox"
      aria-checked={indeterminate ? 'mixed' : on}
      aria-describedby={description ? cid + '-d' : undefined}
      disabled={disabled} className="fgcp-focus"
      onClick={() => set(!on)}
      {...(label ? null : hoverProps)}
      style={{
        flex: 'none', boxSizing: 'border-box', width: px, height: px, padding: 0, margin: 0,
        display: 'grid', placeItems: 'center', borderRadius: 'var(--r-sm)',
        border: '1px solid ' + (filled ? 'var(--acc-graph)' : hover && !disabled ? 'var(--ink-3)' : 'var(--line-strong)'),
        background: filled ? 'var(--acc-graph)' : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
        transition: 'background var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease)',
        ...(label ? null : style),
      }}
      {...(label ? null : rest)}
    >
      <svg viewBox="0 0 12 12" width={px - 4} height={px - 4} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
        {indeterminate
          ? <path d="M2.5 6h7" fill="none" stroke="var(--acc-ink)" strokeWidth="1.8" strokeLinecap="square" />
          : <path d="M2.2 6.3l2.5 2.4 5-5.2" fill="none" stroke="var(--acc-ink)" strokeWidth="1.8"
              strokeLinecap="square" strokeLinejoin="miter" strokeDasharray="12" strokeDashoffset={on ? 0 : 12}
              style={{ transition: 'stroke-dashoffset var(--dur-base) var(--ease)' }} />}
      </svg>
    </button>
  );
  if (!label) return box;
  return (
    <div {...hoverProps} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, ...style }} {...rest}>
      <span style={{ display: 'flex', marginTop: size === 'sm' ? 3 : 2 }}>{box}</span>
      <label htmlFor={cid} style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--fs-body)', lineHeight: 1.45, color: 'var(--ink)' }}>{label}</span>
        {description && <span id={cid + '-d'} style={{ display: 'block', marginTop: 2, fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'var(--ink-3)' }}>{description}</span>}
      </label>
    </div>
  );
}
__FGCP.Checkbox = Checkbox;
})();

// ── components/DataTable/DataTable.jsx
;(() => {
function DataTable({ columns = [], rows = [], footer, style, ...rest }) {
  const [hover, setHover] = React.useState(-1);
  const th = {
    fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-medium)', letterSpacing: 'var(--ls-label)',
    color: 'var(--ink-3)',
    padding: '10px var(--s-4)', borderBottom: '1px solid var(--line)',
    whiteSpace: 'nowrap',
  };
  return (
    // overflow-x on the wrapper: six nowrap columns exceed a narrow panel and
    // would otherwise spill outside the rounded border instead of scrolling.
    <div style={{ overflowX: 'auto', ...style }} {...rest}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        {/* thead is required — browsers repeat it on every printed page */}
        <thead>
          <tr>{columns.map(c => (
            <th key={c.key} style={{ ...th, textAlign: c.align === 'right' ? 'right' : 'left', width: c.width }}>
              {c.header}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.id != null ? row.id : i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(-1)}
              style={{
                background: hover === i ? 'var(--hover)' : 'transparent',
                transition: 'background var(--dur-fast) var(--ease)',
                breakInside: 'avoid',
              }}
            >
              {columns.map(c => (
                <td key={c.key} style={{
                  padding: '11px var(--s-4)',
                  borderBottom: i === rows.length - 1 ? 0 : '1px solid var(--line-soft)',
                  fontSize: 'var(--fs-body)',
                  textAlign: c.align === 'right' ? 'right' : 'left',
                  fontFamily: c.mono ? 'var(--font-num)' : 'var(--font-ui)',
                  fontVariantNumeric: c.mono ? 'tabular-nums' : 'normal',
                  fontWeight: c.strong ? 'var(--fw-medium)' : 'var(--fw-regular)',
                  color: c.strong ? 'var(--ink)' : c.mono ? 'var(--ink-2)' : 'var(--ink)',
                  verticalAlign: 'middle',
                }}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {footer && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 'var(--s-3)',
          padding: '11px var(--s-4)', borderTop: '1px solid var(--line)',
          fontSize: 'var(--fs-sm)', color: 'var(--ink-3)',
        }}>{footer}</div>
      )}
    </div>
  );
}

/** Inline proportion bar for a table cell (lag, quota, utilisation). */
function DataBar({ pct = 0, tone = 'accent', width = 58 }) {
  const TONES = { accent: 'var(--acc-graph)', ok: 'var(--ok)', warn: 'var(--warn)', crit: 'var(--crit)' };
  return (
    <span style={{
      display: 'inline-block', width, height: 4, verticalAlign: 'middle',
      borderRadius: 2, background: 'var(--line)', overflow: 'hidden',
    }}>
      <span style={{
        display: 'block', height: '100%', borderRadius: 2,
        width: Math.max(0, Math.min(100, pct)) + '%',
        background: TONES[tone] || TONES.accent,
      }} />
    </span>
  );
}
__FGCP.DataTable = DataTable; __FGCP.DataBar = DataBar;
})();

// ── components/Field/Field.jsx
;(() => {
function Field({ label, as = 'input', type = 'text', options = [], error, hint, required = false, style, ...rest }) {
  const base = {
    fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-body)',
    padding: '9px 11px',
    border: '1px solid ' + (error ? 'var(--crit)' : 'var(--line-strong)'),
    borderRadius: 'var(--r-ctl)', background: 'var(--surface)',
    outline: 'none', width: '100%', boxSizing: 'border-box',
    color: 'var(--ink)',
  };
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      {label && <span style={{
        fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)',
        color: error ? 'var(--crit)' : 'var(--ink)',
      }}>{label}{required && ' *'}</span>}
      {as === 'textarea'
        ? <textarea rows={4} style={{ ...base, resize: 'vertical' }} {...rest} />
        : as === 'select'
          ? <select style={base} defaultValue="" {...rest}>
              <option value="" disabled>— 선택하세요 —</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          : <input type={type} style={base} {...rest} />}
      {(error || hint) && <span style={{
        fontSize: 'var(--fs-xs)', color: error ? 'var(--crit)' : 'var(--ink-3)',
      }}>{error || hint}</span>}
    </label>
  );
}
__FGCP.Field = Field;
})();

// ── components/LogoMark/LogoMark.jsx
;(() => {
function LogoMark({ size = 32, tone = 'brand', style, ...rest }) {
  const gap = Math.max(1, Math.round(size / 14));
  const empty = tone === 'ghost' ? 'currentColor' : 'transparent';
  return (
    <div
      role="img"
      aria-label="FutureGen C-Planet"
      style={{
        width: size, height: size, display: 'grid',
        gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap,
        flex: 'none', ...style,
      }}
      {...rest}
    >
      <div style={{ background: 'var(--o-500)', borderRadius: 1 }} />
      <div style={{ background: 'var(--o-400)', borderRadius: 1 }} />
      <div style={{ background: empty, opacity: tone === 'ghost' ? 0.12 : 1, borderRadius: 1 }} />
      <div style={{ background: 'var(--b-500)', borderRadius: 1 }} />
    </div>
  );
}
__FGCP.LogoMark = LogoMark;
})();

// ── components/Metric/Metric.jsx
;(() => {
function Metric({
  label, value, unit, delta, tone = 'neutral',
  data, sparkTone, bare = true, size = 'md', style, ...rest
}) {
  const deltaColor = tone === 'up' ? 'var(--ok)' : tone === 'down' ? 'var(--crit)' : 'var(--ink-3)';
  const chrome = bare ? {} : {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-panel)',
    padding: 'var(--s-4)',
    boxShadow: 'var(--elev)',
  };
  return (
    <div style={{ ...chrome, ...style }} {...rest}>
      <div style={{
        fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)', letterSpacing: 'var(--ls-label)',
        color: 'var(--ink-3)',
      }}>{label}</div>
      <div style={{
        fontFamily: 'var(--font-num)', fontVariantNumeric: 'tabular-nums',
        fontSize: size === 'lg' ? '44px' : size === 'sm' ? 'var(--fs-h1)' : 'var(--fs-display)',
        fontWeight: 'var(--fw-medium)',
        letterSpacing: 'var(--ls-display)', lineHeight: 1.05,
        marginTop: 'var(--s-2)', color: 'var(--ink)',
      }}>
        {value}
        {unit && <span style={{
          fontSize: '0.45em', fontWeight: 'var(--fw-medium)',
          color: 'var(--ink-3)', marginLeft: 3,
        }}>{unit}</span>}
      </div>
      {(delta || (data && data.length > 1)) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
          {delta && <span style={{
            fontFamily: 'var(--font-num)', fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-semibold)', color: deltaColor,
          }}>{delta}</span>}
          {data && data.length > 1 && <span style={{ marginLeft: 'auto', display: 'block' }}>
            <Spark data={data} tone={sparkTone || (tone === 'down' ? 'crit' : tone === 'up' ? 'ok' : 'accent')} />
          </span>}
        </div>
      )}
    </div>
  );
}

// Local mirror of Sparkline so Metric has no cross-component import.
function Spark({ data, tone }) {
  const TONES = { accent: 'var(--acc-graph)', ok: 'var(--ok)', warn: 'var(--warn)', crit: 'var(--crit)' };
  const w = 76, hgt = 24;
  const lo = Math.min(...data), hi = Math.max(...data);
  const pad = (hi - lo) * 0.18 || 1;
  const min = lo - pad, max = hi + pad;
  const step = w / (data.length - 1);
  const d = data.map((v, i) => (i ? 'L' : 'M') + (i * step).toFixed(2) + ',' + (hgt - ((v - min) / (max - min)) * hgt).toFixed(2)).join('');
  return (
    <svg viewBox={'0 0 ' + w + ' ' + hgt} width={w} height={hgt} preserveAspectRatio="none" aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      <path d={d} fill="none" stroke={TONES[tone] || TONES.accent} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
__FGCP.Metric = Metric;
})();

// ── components/Overline/Overline.jsx
;(() => {
const TONES = { accent: 'var(--acc)', muted: 'var(--ink-3)', ink: 'var(--ink-2)' };

/** Section label. Pretendard, sentence case — never tracked uppercase. */
function Overline({ tone = 'muted', rule = false, children, style, ...rest }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }} {...rest}>
      {rule && <span aria-hidden="true" style={{ width: 24, height: 3, background: 'var(--acc-graph)' }} />}
      <div style={{
        fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)', letterSpacing: 'var(--ls-label)',
        color: TONES[tone] || TONES.muted,
      }}>{children}</div>
    </div>
  );
}
__FGCP.Overline = Overline;
})();

// ── components/Pill/Pill.jsx
;(() => {
const TONES = {
  ok: 'var(--ok)', warn: 'var(--warn)', crit: 'var(--crit)', info: 'var(--info)',
  accent: 'var(--acc)', neutral: 'var(--ink-3)',
};
const TINTS = {
  ok: 'var(--ok-tint)', warn: 'var(--warn-tint)', crit: 'var(--crit-tint)',
  accent: 'var(--acc-tint)', neutral: 'var(--hover)', info: 'var(--hover)',
};

/** Status marker. Default is a dot + word with no background — the quiet
 *  form. `filled` adds a tint and is for the rare tag that must be found
 *  at a glance (one per view). */
function Pill({ tone = 'neutral', dot = true, filled = false, children, style, ...rest }) {
  const c = TONES[tone] || TONES.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
      color: filled ? c : tone === 'neutral' ? 'var(--ink-2)' : c,
      background: filled ? (TINTS[tone] || TINTS.neutral) : 'transparent',
      padding: filled ? '3px 9px' : 0,
      borderRadius: 'var(--r-sm)', whiteSpace: 'nowrap',
      ...style,
    }} {...rest}>
      {dot && <span aria-hidden="true" style={{ width: 6, height: 6, background: c, flex: 'none' }} />}
      {children}
    </span>
  );
}
__FGCP.Pill = Pill;
})();

// ── components/Progress/Progress.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}

const TONES = { accent: 'var(--acc-graph)', ok: 'var(--ok)', warn: 'var(--warn)', crit: 'var(--crit)', ink: 'var(--ink-2)' };
const HEIGHT = { sm: 2, md: 4, lg: 8 };
const BLOCK = { sm: 4, md: 6, lg: 10 };
const PROG_CSS =
  '@keyframes fgcp-prog-slide{0%{transform:translateX(-100%)}100%{transform:translateX(340%)}}' +
  '@keyframes fgcp-prog-wave{0%,100%{opacity:.16}35%{opacity:1}}' +
  '@keyframes fgcp-prog-pulse{0%,100%{opacity:.3}50%{opacity:.75}}' +
  '.fgcp-prog-ind{animation:fgcp-prog-slide 1.3s var(--ease-inout) infinite}' +
  '.fgcp-prog-wave{animation:fgcp-prog-wave 1.1s var(--ease-inout) infinite}' +
  // reduced motion: no travel, only a slow opacity breath
  '@media (prefers-reduced-motion:reduce){' +
  '.fgcp-prog-ind{animation:fgcp-prog-pulse 1.8s ease-in-out infinite;transform:none!important;width:100%!important}' +
  '.fgcp-prog-wave{animation:fgcp-prog-pulse 1.8s ease-in-out infinite;animation-delay:0s!important}}';

function Progress({
  value, max = 100, label, showValue = true, valueLabel,
  tone = 'accent', size = 'md', variant = 'bar', segments = 24, style, ...rest
}) {
  injectOnce('fgcp-progress', PROG_CSS);
  const indeterminate = value == null;
  const pct = indeterminate ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  const color = TONES[tone] || TONES.accent;
  const auto = React.useId();
  const showHead = label || (showValue && !indeterminate);

  let track;
  if (variant === 'blocks') {
    const n = Math.max(2, segments);
    const filled = Math.round((pct / 100) * n);
    track = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + n + ', minmax(0, 1fr))', gap: 2 }}>
        {Array.from({ length: n }, (_, i) => (
          <span key={i}
            className={indeterminate ? 'fgcp-prog-wave' : undefined}
            style={{
              display: 'block', height: BLOCK[size] || BLOCK.md,
              background: indeterminate || i < filled ? color : 'var(--line)',
              animationDelay: indeterminate ? ((i / n) * 1.1 - 1.1).toFixed(3) + 's' : undefined,
              transition: indeterminate ? undefined : 'background var(--dur-fast) var(--ease) ' + (i * 6) + 'ms',
            }} />
        ))}
      </div>
    );
  } else {
    track = (
      <div style={{ position: 'relative', height: HEIGHT[size] || HEIGHT.md, background: 'var(--line)', overflow: 'hidden' }}>
        {indeterminate
          ? <span className="fgcp-prog-ind" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '30%', background: color }} />
          : <span style={{ display: 'block', height: '100%', width: pct + '%', background: color, transition: 'width var(--dur-slow) var(--ease)' }} />}
      </div>
    );
  }

  return (
    <div role="progressbar"
      aria-labelledby={label ? auto : undefined}
      aria-label={label ? undefined : 'Progress'}
      aria-valuemin={0} aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : Math.round(pct)}
      aria-valuetext={!indeterminate && valueLabel != null ? String(valueLabel) : undefined}
      style={{ minWidth: 0, ...style }} {...rest}>
      {showHead && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
          {label && <span id={auto} style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', minWidth: 0 }}>{label}</span>}
          {showValue && !indeterminate && (
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-num)', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--fs-sm)', color: 'var(--ink)', whiteSpace: 'nowrap' }}>
              {valueLabel != null ? valueLabel : Math.round(pct) + '%'}
            </span>
          )}
        </div>
      )}
      {track}
    </div>
  );
}
__FGCP.Progress = Progress;
})();

// ── components/RadioGroup/RadioGroup.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}
function norm(options) {
  return options.map(o => (typeof o === 'object' && o !== null ? o : { value: o, label: String(o) }));
}

/** The one round control in the system: the circle/square distinction from
 *  Checkbox is a stronger convention than the brand's right angle. */
function RadioGroup({
  options = [], value, defaultValue = null, onChange,
  orientation = 'vertical', disabled = false, label, style, ...rest
}) {
  injectOnce('fgcp-focus', FOCUS_CSS);
  const items = norm(options);
  const [cur, set] = useControl(value, defaultValue, onChange);
  const refs = React.useRef([]);
  const auto = React.useId();
  const enabled = i => !disabled && !items[i].disabled;
  const step = (from, dir) => {
    let i = from;
    for (let k = 0; k < items.length; k++) { i = (i + dir + items.length) % items.length; if (enabled(i)) return i; }
    return from;
  };
  const onKey = (e, i) => {
    const fwd = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const back = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
    if (!fwd && !back) return;
    e.preventDefault();
    const n = step(i, fwd ? 1 : -1);
    set(items[n].value);
    if (refs.current[n]) refs.current[n].focus();
  };
  const sel = items.findIndex(o => o.value === cur);
  const tabStop = sel >= 0 ? sel : items.findIndex((o, i) => enabled(i));
  const horiz = orientation === 'horizontal';

  return (
    <div role="radiogroup" aria-labelledby={label ? auto + '-l' : undefined} aria-orientation={orientation}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }} {...rest}>
      {label && <div id={auto + '-l'} style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--ink-2)' }}>{label}</div>}
      <div style={{ display: 'flex', flexDirection: horiz ? 'row' : 'column', flexWrap: 'wrap', gap: horiz ? 20 : 12 }}>
        {items.map((o, i) => {
          const on = i === sel;
          const dis = !enabled(i);
          const oid = auto + '-' + i;
          return (
            <div key={String(o.value)} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <button
                ref={el => { refs.current[i] = el; }}
                id={oid} type="button" role="radio" aria-checked={on}
                aria-describedby={o.description ? oid + '-d' : undefined}
                disabled={dis} tabIndex={i === tabStop ? 0 : -1} className="fgcp-focus"
                onClick={() => set(o.value)} onKeyDown={e => onKey(e, i)}
                style={{
                  flex: 'none', boxSizing: 'border-box', width: 16, height: 16, marginTop: 2, padding: 0,
                  display: 'grid', placeItems: 'center', borderRadius: '50%', background: 'transparent',
                  border: '1px solid ' + (on ? 'var(--acc-graph)' : 'var(--line-strong)'),
                  cursor: dis ? 'not-allowed' : 'pointer', opacity: dis ? 0.45 : 1,
                  transition: 'border-color var(--dur-fast) var(--ease)',
                }}
              >
                <span aria-hidden="true" style={{
                  width: 8, height: 8, borderRadius: '50%', background: 'var(--acc-graph)',
                  transform: on ? 'scale(1)' : 'scale(0)', transition: 'transform var(--dur-base) var(--ease)',
                }} />
              </button>
              <label htmlFor={oid} style={{ cursor: dis ? 'not-allowed' : 'pointer', opacity: dis ? 0.55 : 1 }}>
                <span style={{ display: 'block', fontSize: 'var(--fs-body)', lineHeight: 1.45, color: 'var(--ink)' }}>{o.label}</span>
                {o.description && <span id={oid + '-d'} style={{ display: 'block', marginTop: 2, fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'var(--ink-3)' }}>{o.description}</span>}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
__FGCP.RadioGroup = RadioGroup;
})();

// ── components/Section/Section.jsx
;(() => {
function Section({ label, title, intro, tone = 'default', pad, children, style, ...rest }) {
  const bg = tone === 'subtle' ? 'var(--surface-2)' : tone === 'inverse' ? 'var(--n-1000)' : 'transparent';
  return (
    <section style={{
      padding: (pad != null ? pad + 'px' : 'var(--s-9)') + ' var(--s-6)',
      background: bg,
      color: tone === 'inverse' ? 'var(--n-100)' : 'inherit',
      ...style,
    }} {...rest}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {(label || title || intro) && (
          <div style={{ marginBottom: 'var(--s-7)', display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
            <span style={{ display: 'inline-block', width: 40, height: 3, background: 'var(--acc-graph)', borderRadius: 2 }} />
            {label && <div style={{
              fontFamily: 'var(--font-num)', fontSize: 'var(--fs-micro)',
              fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-label)',
              textTransform: 'uppercase', color: 'var(--acc)',
            }}>{label}</div>}
            {title && <h2 style={{
              margin: 0, fontFamily: 'var(--font-display)', fontSize: 'var(--fs-h1)',
              fontWeight: 'var(--fw-bold)', lineHeight: 'var(--lh-heading)',
              letterSpacing: 'var(--ls-heading)', textWrap: 'balance',
            }}>{title}</h2>}
            {intro && <p style={{
              margin: 0, fontSize: 'var(--fs-lede)', lineHeight: 'var(--lh-body)',
              maxWidth: '46em', color: 'var(--ink-2)',
            }}>{intro}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}
__FGCP.Section = Section;
})();

// ── components/SegmentedControl/SegmentedControl.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}
function norm(options) {
  return options.map(o => (typeof o === 'object' && o !== null ? o : { value: o, label: String(o) }));
}

const SEG_CSS = '.fgcp-seg{color:var(--ink-3)}.fgcp-seg:hover:not(:disabled){color:var(--ink-2)}.fgcp-seg[aria-checked=true],.fgcp-seg[aria-checked=true]:hover{color:var(--ink)}';

function SegmentedControl({
  options = [], value, defaultValue, onChange, size = 'md',
  fullWidth = false, disabled = false, style, ...rest
}) {
  injectOnce('fgcp-focus', FOCUS_CSS);
  injectOnce('fgcp-seg', SEG_CSS);
  const items = norm(options);
  const [cur, set] = useControl(value, defaultValue !== undefined ? defaultValue : (items[0] ? items[0].value : null), onChange);
  const wrap = React.useRef(null);
  const refs = React.useRef([]);
  const [ind, setInd] = React.useState(null);
  const [animate, setAnimate] = React.useState(false);
  const idx = Math.max(0, items.findIndex(o => o.value === cur));

  const measure = React.useCallback(() => {
    const el = refs.current[idx];
    if (el) setInd({ x: el.offsetLeft, w: el.offsetWidth });
  }, [idx, items.length]);
  React.useLayoutEffect(() => { measure(); }, [measure]);
  React.useEffect(() => {
    // Enable the slide only after the first placement, so it never animates in from 0.
    const t = setTimeout(() => setAnimate(true), 0);
    let ro;
    if (typeof ResizeObserver !== 'undefined' && wrap.current) { ro = new ResizeObserver(measure); ro.observe(wrap.current); }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); };
  }, [measure]);

  const enabled = i => !disabled && !items[i].disabled;
  const onKey = (e, i) => {
    const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    let n = i;
    for (let k = 0; k < items.length; k++) { n = (n + dir + items.length) % items.length; if (enabled(n)) break; }
    set(items[n].value);
    if (refs.current[n]) refs.current[n].focus();
  };
  const H = size === 'sm' ? 26 : 30;

  return (
    <div ref={wrap} role="radiogroup" style={{
      position: 'relative', display: fullWidth ? 'flex' : 'inline-flex', boxSizing: 'border-box',
      height: H, padding: 2, border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)',
      background: 'var(--hover)', opacity: disabled ? 0.45 : 1, verticalAlign: 'middle', ...style,
    }} {...rest}>
      {ind && <span aria-hidden="true" style={{
        position: 'absolute', top: 2, bottom: 2, left: 0, width: ind.w, boxSizing: 'border-box',
        transform: 'translateX(' + ind.x + 'px)',
        background: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-sm)',
        transition: animate ? 'transform var(--dur-base) var(--ease), width var(--dur-base) var(--ease)' : 'none',
      }} />}
      {items.map((o, i) => {
        const on = i === idx;
        return (
          <button
            key={String(o.value)} ref={el => { refs.current[i] = el; }}
            type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
            disabled={!enabled(i)} className="fgcp-focus fgcp-seg"
            onClick={() => set(o.value)} onKeyDown={e => onKey(e, i)}
            style={{
              position: 'relative', zIndex: 1, flex: fullWidth ? 1 : 'none', boxSizing: 'border-box',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              height: '100%', margin: 0, padding: size === 'sm' ? '0 10px' : '0 13px',
              border: 0, background: 'transparent', borderRadius: 'var(--r-sm)',
              font: 'inherit', fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-sm)',
              // weight is constant across states — changing it would shift widths under the indicator
              fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
              cursor: enabled(i) ? 'pointer' : 'not-allowed',
              transition: 'color var(--dur-fast) var(--ease)',
            }}
          >
            {o.label}
            {o.count != null && <span style={{ fontFamily: 'var(--font-num)', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-regular)', color: 'var(--ink-3)' }}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
__FGCP.SegmentedControl = SegmentedControl;
})();

// ── components/Skeleton/Skeleton.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}

const SKEL_CSS =
  '@keyframes fgcp-skel{0%,100%{opacity:1}50%{opacity:.45}}' +
  '.fgcp-skel{animation:fgcp-skel 1.6s var(--ease-inout) infinite}' +
  '@media (prefers-reduced-motion:reduce){.fgcp-skel{animation:none}}';

/** Loading placeholder. A slow pulse only — no shimmer gradient. */
function Skeleton({ width = '100%', height = 12, lines, gap = 8, style, ...rest }) {
  injectOnce('fgcp-skeleton', SKEL_CSS);
  const bar = (w, k) => (
    <span key={k} className="fgcp-skel" style={{
      display: 'block', width: w, height, background: 'var(--line-strong)', borderRadius: 'var(--r-sm)',
    }} />
  );
  if (lines && lines > 1) {
    return (
      <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap, ...style }} {...rest}>
        {Array.from({ length: lines }, (_, i) => bar(i === lines - 1 ? '62%' : width, i))}
      </div>
    );
  }
  return (
    <span aria-hidden="true" className="fgcp-skel" style={{
      display: 'block', width, height, background: 'var(--line-strong)', borderRadius: 'var(--r-sm)', ...style,
    }} {...rest} />
  );
}
__FGCP.Skeleton = Skeleton;
})();

// ── components/Sparkline/Sparkline.jsx
;(() => {
const TONES = {
  accent: 'var(--acc-graph)', ok: 'var(--ok)', warn: 'var(--warn)',
  crit: 'var(--crit)', info: 'var(--info)', muted: 'var(--ink-3)',
};

function Sparkline({
  data = [], tone = 'accent', width = 76, height = 24,
  area = false, strokeWidth = 1.5, style, ...rest
}) {
  if (!data || data.length < 2) return null;
  const color = TONES[tone] || TONES.accent;

  // Pad the y-domain by a fraction of the RANGE, never of the absolute value —
  // padding by value collapses the line into a fraction of the canvas.
  const lo = Math.min(...data), hi = Math.max(...data);
  const pad = (hi - lo) * 0.18 || 1;
  const min = lo - pad, max = hi + pad;

  const step = width / (data.length - 1);
  const y = v => height - ((v - min) / (max - min)) * height;
  const d = data.map((v, i) => (i ? 'L' : 'M') + (i * step).toFixed(2) + ',' + y(v).toFixed(2)).join('');
  const gid = 'spark-' + tone + '-' + width + 'x' + height;

  return (
    <svg
      viewBox={'0 0 ' + width + ' ' + height}
      width={width} height={height}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ display: 'block', overflow: 'visible', ...style }}
      {...rest}
    >
      {area && (
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.20" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {area && <path d={d + 'L' + width + ',' + height + 'L0,' + height + 'Z'} fill={'url(#' + gid + ')'} />}
      <path
        d={d} fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
__FGCP.Sparkline = Sparkline;
})();

// ── components/Spinner/Spinner.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}

const SPIN_CSS =
  '@keyframes fgcp-spin-q{0%{opacity:1}55%,100%{opacity:.16}}' +
  '.fgcp-spin-q{animation:fgcp-spin-q .9s linear infinite}' +
  '@media (prefers-reduced-motion:reduce){.fgcp-spin-q{animation-duration:2.4s}}';
// DOM order is TL, TR, BL, BR; the light travels clockwise TL → TR → BR → BL.
const STEP = [0, 1, 3, 2];

/** The mark, lit one quadrant at a time. */
function Spinner({ size = 16, tone = 'accent', label = '불러오는 중', style, ...rest }) {
  injectOnce('fgcp-spinner', SPIN_CSS);
  const color = tone === 'current' ? 'currentColor' : tone === 'ink' ? 'var(--ink-2)' : 'var(--acc-graph)';
  const gap = Math.max(1, Math.round(size / 8));
  return (
    <span role="status" aria-label={label} style={{
      display: 'inline-grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap,
      width: size, height: size, flex: 'none', verticalAlign: 'middle', ...style,
    }} {...rest}>
      {STEP.map((s, i) => (
        <span key={i} className="fgcp-spin-q" style={{ background: color, animationDelay: (s * 0.225 - 0.9).toFixed(3) + 's' }} />
      ))}
    </span>
  );
}
__FGCP.Spinner = Spinner;
})();

// ── components/Stat/Stat.jsx
;(() => {
function Stat({ value, label, delta, tone = 'neutral', size = 'md', style, ...rest }) {
  const col = tone === 'up' ? 'var(--ok)' : tone === 'down' ? 'var(--crit)' : 'var(--ink-3)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }} {...rest}>
      <div style={{
        fontFamily: 'var(--font-num)', fontVariantNumeric: 'tabular-nums',
        fontSize: size === 'lg' ? 'var(--fs-display)' : 'var(--fs-h1)',
        fontWeight: 'var(--fw-semibold)',
        letterSpacing: 'var(--ls-display)', lineHeight: 1.05, color: 'var(--ink)',
      }}>{value}</div>
      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', lineHeight: 1.5 }}>{label}</div>
      {delta && <div style={{
        fontFamily: 'var(--font-num)', fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)', color: col,
      }}>{delta}</div>}
    </div>
  );
}
__FGCP.Stat = Stat;
})();

// ── components/StatTile/StatTile.jsx
;(() => {
/** Editorial hero figure. No box: a hairline above, the figure in --font-ed. */
function StatTile({ value, label, note, style, ...rest }) {
  return (
    <div style={{ paddingTop: 'var(--s-3)', borderTop: '1px solid var(--line)', breakInside: 'avoid', ...style }} {...rest}>
      <div style={{
        fontFamily: 'var(--font-ed)', fontWeight: 'var(--fw-regular)',
        fontSize: 'var(--fs-display)', letterSpacing: '-.02em',
        lineHeight: 1.02, color: 'var(--ink)',
      }}>{value}</div>
      {label && <div style={{ marginTop: 'var(--s-2)', fontSize: 'var(--fs-sm)', lineHeight: 1.55, color: 'var(--ink-2)' }}>{label}</div>}
      {note && <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{note}</div>}
    </div>
  );
}
__FGCP.StatTile = StatTile;
})();

// ── components/Switch/Switch.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}

const SIZES = { sm: { w: 26, h: 14, t: 8 }, md: { w: 34, h: 18, t: 12 } };

/** Square track, square thumb — the mark is right-angled, so is this. */
function Switch({
  checked, defaultChecked = false, onChange, disabled = false,
  size = 'md', label, description, id, style, ...rest
}) {
  injectOnce('fgcp-focus', FOCUS_CSS);
  const [on, set] = useControl(checked, defaultChecked, onChange);
  const auto = React.useId();
  const cid = id || auto;
  const S = SIZES[size] || SIZES.md;
  const pad = (S.h - 2 - S.t) / 2;
  const travel = S.w - 2 - S.t - pad * 2;

  const control = (
    <button
      id={cid} type="button" role="switch" aria-checked={on}
      aria-describedby={description ? cid + '-d' : undefined}
      disabled={disabled} className="fgcp-focus"
      onClick={() => set(!on)}
      style={{
        position: 'relative', flex: 'none', boxSizing: 'border-box',
        width: S.w, height: S.h, padding: 0, margin: 0,
        borderRadius: 'var(--r-ctl)',
        border: '1px solid ' + (on ? 'var(--acc-graph)' : 'var(--line-strong)'),
        background: on ? 'var(--acc-graph)' : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'background var(--dur-base) var(--ease), border-color var(--dur-base) var(--ease)',
        ...(label ? null : style),
      }}
      {...(label ? null : rest)}
    >
      <span aria-hidden="true" style={{
        position: 'absolute', top: pad, left: pad, width: S.t, height: S.t, borderRadius: 1,
        background: on ? 'var(--acc-ink)' : 'var(--ink-3)',
        transform: 'translateX(' + (on ? travel : 0) + 'px)',
        transition: 'transform var(--dur-base) var(--ease), background var(--dur-base) var(--ease)',
      }} />
    </button>
  );
  if (!label) return control;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, ...style }} {...rest}>
      <span style={{ display: 'flex', marginTop: size === 'sm' ? 3 : 1 }}>{control}</span>
      <label htmlFor={cid} style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--fs-body)', lineHeight: 1.45, color: 'var(--ink)' }}>{label}</span>
        {description && <span id={cid + '-d'} style={{ display: 'block', marginTop: 2, fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'var(--ink-3)' }}>{description}</span>}
      </label>
    </div>
  );
}
__FGCP.Switch = Switch;
})();

// ── components/Tabs/Tabs.jsx
;(() => {
function injectOnce(id, css) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}
const FOCUS_CSS = '.fgcp-focus:focus{outline:none}.fgcp-focus:focus-visible{outline:2px solid var(--acc-graph);outline-offset:2px}';
function useControl(value, initial, onChange) {
  const [inner, setInner] = React.useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const set = next => { if (!controlled) setInner(next); if (onChange) onChange(next); };
  return [current, set];
}
function norm(options) {
  return options.map(o => (typeof o === 'object' && o !== null ? o : { value: o, label: String(o) }));
}

const TAB_CSS = '.fgcp-tab{color:var(--ink-3)}.fgcp-tab:hover:not(:disabled){color:var(--ink-2)}.fgcp-tab[aria-selected=true],.fgcp-tab[aria-selected=true]:hover{color:var(--ink)}';

/** Tab strip only. Render the active panel yourself from `value`;
 *  pass `controls` on an item to wire aria-controls to its panel id. */
function Tabs({ items = [], value, defaultValue, onChange, size = 'md', style, ...rest }) {
  injectOnce('fgcp-focus', FOCUS_CSS);
  injectOnce('fgcp-tabs', TAB_CSS);
  const list = norm(items);
  const [cur, set] = useControl(value, defaultValue !== undefined ? defaultValue : (list[0] ? list[0].value : null), onChange);
  const wrap = React.useRef(null);
  const refs = React.useRef([]);
  const [ind, setInd] = React.useState(null);
  const [animate, setAnimate] = React.useState(false);
  const idx = Math.max(0, list.findIndex(o => o.value === cur));

  const measure = React.useCallback(() => {
    const el = refs.current[idx];
    if (el) setInd({ x: el.offsetLeft, w: el.offsetWidth });
  }, [idx, list.length]);
  React.useLayoutEffect(() => { measure(); }, [measure]);
  React.useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 0);
    let ro;
    if (typeof ResizeObserver !== 'undefined' && wrap.current) { ro = new ResizeObserver(measure); ro.observe(wrap.current); }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); };
  }, [measure]);

  const enabled = i => !list[i].disabled;
  const onKey = (e, i) => {
    let n = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      n = i;
      for (let k = 0; k < list.length; k++) { n = (n + dir + list.length) % list.length; if (enabled(n)) break; }
    } else if (e.key === 'Home') n = list.findIndex((o, j) => enabled(j));
    else if (e.key === 'End') { for (let j = list.length - 1; j >= 0; j--) if (enabled(j)) { n = j; break; } }
    if (n < 0) return;
    e.preventDefault();
    set(list[n].value);
    if (refs.current[n]) refs.current[n].focus();
  };

  return (
    <div ref={wrap} role="tablist" style={{
      position: 'relative', display: 'flex', gap: size === 'sm' ? 18 : 24,
      borderBottom: '1px solid var(--line)', overflowX: 'auto', scrollbarWidth: 'none', ...style,
    }} {...rest}>
      {list.map((o, i) => {
        const on = i === idx;
        return (
          <button
            key={String(o.value)} ref={el => { refs.current[i] = el; }}
            type="button" role="tab" aria-selected={on} aria-controls={o.controls}
            tabIndex={on ? 0 : -1} disabled={!enabled(i)} className="fgcp-focus fgcp-tab"
            onClick={() => set(o.value)} onKeyDown={e => onKey(e, i)}
            style={{
              flex: 'none', display: 'inline-flex', alignItems: 'baseline', gap: 6,
              margin: 0, padding: size === 'sm' ? '6px 0 9px' : '8px 0 11px',
              border: 0, background: 'transparent', font: 'inherit', fontFamily: 'var(--font-ui)',
              fontSize: size === 'sm' ? 'var(--fs-sm)' : 'var(--fs-body)',
              fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
              cursor: enabled(i) ? 'pointer' : 'not-allowed', opacity: enabled(i) ? 1 : 0.45,
              transition: 'color var(--dur-fast) var(--ease)',
            }}
          >
            {o.label}
            {o.count != null && <span style={{ fontFamily: 'var(--font-num)', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-regular)', color: 'var(--ink-3)' }}>{o.count}</span>}
          </button>
        );
      })}
      {ind && <span aria-hidden="true" style={{
        position: 'absolute', left: 0, bottom: 0, height: 2, width: ind.w,
        transform: 'translateX(' + ind.x + 'px)', background: 'var(--acc-graph)',
        transition: animate ? 'transform var(--dur-base) var(--ease), width var(--dur-base) var(--ease)' : 'none',
      }} />}
    </div>
  );
}
__FGCP.Tabs = Tabs;
})();

export const { AlertRow, Button, Callout, Card, Checkbox, DataBar, DataTable, Field, LogoMark, Metric, Overline, Pill, Progress, RadioGroup, Section, SegmentedControl, Skeleton, Sparkline, Spinner, Stat, StatTile, Switch, Tabs } = __FGCP;
